




































































































import { Component, Vue } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { Account, Permission, AccountManager, AccountActions, Action, EditorialProject, App, AccountTeam } from '@/models'
import { ActionTypes } from '@/store/action-types'
import { CvValueUtils } from '@/utils/cv-value-utils'
import { CV_VALUE_COUNTRY, CV_VALUE_EDITORIAL_PROJECT_BUSINESS_UNIT, ACCOUNT_PROFILE_ADMIN, APP_CCP, ROLE_ADMIN } from '@/utils/constants'
import AccountHeader from '@/components/AccountHeader.vue'
import AccountPermissions from '@/components/AccountPermissions.vue'
import AccountResources from '@/components/AccountResources.vue'
import AccountActionsComponent from '@/components/AccountActions.vue'
import AccountWorkers from '@/components/AccountWorkers.vue'
import AccountTeams from '@/components/AccountTeams.vue'
import AccountNavigation from '@/components/AccountNavigation.vue'
import AccountAudit from '@/components/AccountAudit.vue'

@Component({
  components: {
    AccountHeader,
    AccountPermissions,
    AccountResources,
    AccountActions: AccountActionsComponent,
    AccountWorkers,
    AccountTeams,
    AccountNavigation: AccountNavigation,
    AccountAudit
  }
})
export default class AccountDetail extends Vue {
  private account: Account = {
    accountId: '',
    firstName: '',
    lastName: '',
    createdOn: '',
    updatedOn: ''
  }

  private loading = false
  private routeAccountId = this.$router.currentRoute.params.accountId || ''
  private apps: App[] = []
  private allActions: Action[] = []
  private lastManagerSearch = ''
  private allowedEditorialProjectIds: string[] | null = null

  get editorialProjects(): EditorialProject[] {
    return this.$store.getters.editorialProjects
  }

  get permissions(): Permission[] {
    return this.account.permissions || []
  }

  get workers(): AccountManager[] {
    return this.account.workers || []
  }

  get actions(): AccountActions[] {
    return this.account.actions || []
  }

  get teams(): AccountTeam[] {
    return this.account.teams || []
  }

  async created() {
    this.loadCountries()
    this.loadBusinessUnits()
    await Promise.all([
      this.$store.dispatch(ActionTypes.LOAD_EDITORIAL_PROJECTS),
      this.loadApps(),
      this.loadActions(),
      this.loadAccount(),
      this.loadAllowedEditorialProjects()
    ])
  }

  async loadAllowedEditorialProjects() {
    const currentUser = this.$store.state.user
    if (currentUser?.account?.profile === ACCOUNT_PROFILE_ADMIN) {
      this.allowedEditorialProjectIds = null
      return
    }
    const currentAccountId = currentUser?.account?.accountId
    if (!currentAccountId) {
      this.allowedEditorialProjectIds = []
      return
    }
    try {
      const me = await ApiClient.getAccount(currentAccountId)
      const ccpPermissions = (me.permissions || []).filter(p => p.appId === APP_CCP)
      if (ccpPermissions.some(p => p.role === ROLE_ADMIN)) {
        this.allowedEditorialProjectIds = null
      } else {
        this.allowedEditorialProjectIds = Array.from(
          new Set(ccpPermissions.map(p => p.editorialProjectId).filter((id): id is string => !!id))
        )
      }
    } catch (error) {
      console.error('Error loading current user editorial projects:', error)
      this.allowedEditorialProjectIds = []
    }
  }

  async loadApps() {
    try {
      this.apps = await ApiClient.getApps()
    } catch (error) {
      console.error('Error loading apps:', error)
      this.$bvToast.toast(this.$t('account_detail.permissions.messages.error_loading_apps').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  async loadAccount() {
    if (this.routeAccountId) {
      try {
        this.loading = true
        this.account = await ApiClient.getAccount(this.routeAccountId)
      } catch (error) {
        console.error('Error loading account:', error)
        this.$bvToast.toast(this.$t('account_detail.error_loading').toString(), {
          title: this.$t('account_detail.error_title').toString(),
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    }
  }

  async loadCountries() {
    await CvValueUtils.getCvValuesByType(CV_VALUE_COUNTRY)
  }

  async loadBusinessUnits() {
    await CvValueUtils.getCvValuesByType(CV_VALUE_EDITORIAL_PROJECT_BUSINESS_UNIT)
  }

  async loadActions() {
    try {
      this.allActions = await ApiClient.getActions()
    } catch (error) {
      console.error('Error loading actions:', error)
      this.$bvToast.toast(this.$t('account_detail.actions.messages.error_loading').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  // Permissions Methods
  async addPermission(payload: { appId: string; form: { editorialProjectIds: string[]; role: string }; setAdding: (value: boolean) => void }) {
    const { appId, form, setAdding } = payload

    if (!form || !form.editorialProjectIds || form.editorialProjectIds.length === 0 || !form.role) {
      this.$bvToast.toast(this.$t('account_detail.permissions.messages.select_project').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'warning',
        solid: true
      })
      return
    }

    setAdding(true)

    try {
      let successCount = 0
      let failCount = 0
      const errors: string[] = []

      for (const projectId of form.editorialProjectIds) {
        try {
          const newPermission: Permission = {
            permissionId: 0,
            accountId: this.account.accountId,
            appId: appId,
            role: form.role,
            editorialProjectId: projectId
          }

          await ApiClient.createPermission(newPermission)
          successCount++
        } catch (error: any) {
          failCount++
          const projectName = this.getEditorialProjectName(projectId)
          errors.push(projectName)
          console.error(`Error adding permission for project ${projectId}:`, error)
        }
      }

      if (successCount > 0) {
        const message = successCount === 1
          ? this.$t('account_detail.permissions.messages.permission_added').toString()
          : this.$t('account_detail.permissions.messages.permissions_added', { count: successCount }).toString()

        this.$bvToast.toast(message, {
          title: this.$t('feedback.feedback_message_success_title').toString(),
          variant: 'success',
          solid: true
        })
      }

      if (failCount > 0) {
        const message = failCount === 1
          ? this.$t('account_detail.permissions.messages.error_adding_permission', { project: errors[0] }).toString()
          : this.$t('account_detail.permissions.messages.error_adding_permissions', { count: failCount, projects: errors.join(', ') }).toString()

        this.$bvToast.toast(message, {
          title: this.$t('account_detail.error_title').toString(),
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      }

      // Clear form
      const permissionsComponent = this.$refs.permissionsComponent as InstanceType<typeof AccountPermissions>
      if (permissionsComponent) {
        permissionsComponent.clearPermissionForm(appId)
      }

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error adding permissions:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.permissions.messages.error_adding').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      setAdding(false)
    }
  }

  async updatePermissionRole(payload: { permission: Permission; newRole: string }) {
    const { permission, newRole } = payload

    if (newRole === permission.role) {
      return
    }

    try {
      const updatedPermission: Permission = {
        ...permission,
        role: newRole
      }

      await ApiClient.updatePermission(permission.permissionId, updatedPermission)

      this.$bvToast.toast(this.$t('account_detail.permissions.messages.role_updated').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true,
        autoHideDelay: 2000
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error updating permission:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.permissions.messages.error_updating_role').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  async deletePermission(permission: Permission) {
    const projectName = this.getEditorialProjectName(permission.editorialProjectId)
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_detail.permissions.messages.confirm_delete', { project: projectName }).toString(),
      {
        title: this.$t('account_detail.permissions.messages.confirm_delete_title').toString(),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      await ApiClient.deletePermission(permission.permissionId)

      this.$bvToast.toast(this.$t('account_detail.permissions.messages.permission_deleted').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error deleting permission:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.permissions.messages.error_deleting').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  async addAdminPermission(appId: string) {
    try {
      const newPermission: Permission = {
        permissionId: 0,
        accountId: this.account.accountId,
        appId: appId,
        role: 'ADMIN',
        editorialProjectId: null
      }

      await ApiClient.createPermission(newPermission)

      this.$bvToast.toast(this.$t('account_detail.permissions.messages.admin_permission_added').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true,
        autoHideDelay: 2000
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error adding admin permission:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.permissions.messages.error_adding_admin').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  async addAccessPermission(appId: string) {
    try {
      const newPermission: Permission = {
        permissionId: 0,
        accountId: this.account.accountId,
        appId: appId,
        role: 'ACCESS',
        editorialProjectId: null
      }

      await ApiClient.createPermission(newPermission)

      this.$bvToast.toast(this.$t('account_detail.permissions.messages.access_permission_added').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true,
        autoHideDelay: 2000
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error adding access permission:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.permissions.messages.error_adding_access').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  async deleteAllPermissions(appId: string) {
    const appPermissions = this.permissions.filter(p => p.appId === appId)

    if (appPermissions.length === 0) return

    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_detail.permissions.messages.confirm_delete_all', { count: appPermissions.length }).toString(),
      {
        title: this.$t('account_detail.permissions.messages.confirm_delete_all_title').toString(),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      let failCount = 0

      for (const permission of appPermissions) {
        try {
          await ApiClient.deletePermission(permission.permissionId)
        } catch (error) {
          failCount++
          console.error(`Error deleting permission ${permission.permissionId}:`, error)
        }
      }

      if (failCount === 0) {
        this.$bvToast.toast(this.$t('account_detail.permissions.messages.all_permissions_deleted').toString(), {
          title: this.$t('feedback.feedback_message_success_title').toString(),
          variant: 'success',
          solid: true
        })
      } else {
        this.$bvToast.toast(this.$t('account_detail.permissions.messages.error_deleting_some', { count: failCount }).toString(), {
          title: this.$t('account_detail.error_title').toString(),
          variant: 'danger',
          solid: true
        })
      }

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error deleting all permissions:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.permissions.messages.error_deleting').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  getEditorialProjectName(projectId: string | null): string {
    if (!projectId) return 'N/A'
    const project = this.editorialProjects.find((ep: EditorialProject) => ep.id === projectId)
    return project ? project.name : projectId
  }

  // Actions Methods
  async addActions(payload: { selectedActionIds: string[]; setAdding: (value: boolean) => void; clearSelection: () => void }) {
    const { selectedActionIds, setAdding, clearSelection } = payload

    if (selectedActionIds.length === 0) {
      this.$bvToast.toast(this.$t('account_detail.actions.messages.select_action').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'warning',
        solid: true
      })
      return
    }

    setAdding(true)

    try {
      let successCount = 0
      let failCount = 0
      const errors: string[] = []

      for (const actionsId of selectedActionIds) {
        try {
          await ApiClient.createAccountAction(this.account.accountId, actionsId)
          successCount++
        } catch (error: any) {
          failCount++
          const actionDesc = this.getActionDescription(actionsId)
          errors.push(actionDesc)
          console.error(`Error adding action ${actionsId}:`, error)
        }
      }

      if (successCount > 0) {
        const message = successCount === 1
          ? this.$t('account_detail.actions.messages.action_added').toString()
          : this.$t('account_detail.actions.messages.actions_added', { count: successCount }).toString()

        this.$bvToast.toast(message, {
          title: this.$t('feedback.feedback_message_success_title').toString(),
          variant: 'success',
          solid: true
        })
      }

      if (failCount > 0) {
        const message = failCount === 1
          ? this.$t('account_detail.actions.messages.error_adding_action', { action: errors[0] }).toString()
          : this.$t('account_detail.actions.messages.error_adding_actions', { count: failCount, actions: errors.join(', ') }).toString()

        this.$bvToast.toast(message, {
          title: this.$t('account_detail.error_title').toString(),
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      }

      clearSelection()
      await this.loadAccount()
    } catch (error: any) {
      console.error('Error adding actions:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.actions.messages.error_adding').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      setAdding(false)
    }
  }

  async deleteAction(accountAction: AccountActions) {
    const actionDesc = this.getActionDescription(accountAction.actionsId)
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_detail.actions.messages.confirm_delete', { action: actionDesc }).toString(),
      {
        title: this.$t('account_detail.actions.messages.confirm_delete_title').toString(),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      await ApiClient.deleteAccountAction(accountAction.accountActionsId)

      this.$bvToast.toast(this.$t('account_detail.actions.messages.action_deleted').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error deleting action:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.actions.messages.error_deleting').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  async deleteAllActions() {
    if (!this.actions || this.actions.length === 0) return

    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_detail.actions.messages.confirm_delete_all', { count: this.actions.length }).toString(),
      {
        title: this.$t('account_detail.actions.messages.confirm_delete_all_title').toString(),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      for (const action of this.actions) {
        await ApiClient.deleteAccountAction(action.accountActionsId)
      }

      this.$bvToast.toast(this.$t('account_detail.actions.messages.all_actions_deleted').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error deleting all actions:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.actions.messages.error_deleting_all').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })

      await this.loadAccount()
    }
  }

  getActionDescription(actionsId: string): string {
    const action = this.allActions.find(a => a.actionsId === actionsId)
    return action ? action.description : actionsId
  }

  // Workers Methods
  async onManagerSearch(payload: { search: string; loading: (value: boolean) => void; setResults: (results: Account[]) => void; setSearchLength: (length: number) => void }) {
    const { search, loading, setResults, setSearchLength } = payload

    setSearchLength(search ? search.length : 0)

    if (!search || search.length < 2) {
      if (this.lastManagerSearch.length >= 2) {
        return
      }
      setResults(this.getSelectedAccounts())
      return
    }

    this.lastManagerSearch = search

    loading(true)
    try {
      const result = await ApiClient.searchAccounts(search, 20, 0)
      const assignedEmployeeIds = this.workers.map(w => w.accountId)
      const filteredResults = result.items.filter(
        account => !assignedEmployeeIds.includes(account.accountId) && account.accountId !== this.account.accountId
      )

      const selectedAccounts = this.getSelectedAccounts()
      const allAccountIds = new Set([...filteredResults.map(a => a.accountId), ...selectedAccounts.map(a => a.accountId)])
      const mergedResults = Array.from(allAccountIds).map(id => {
        return filteredResults.find(a => a.accountId === id) || selectedAccounts.find(a => a.accountId === id)
      }).filter(a => a !== undefined) as Account[]

      setResults(mergedResults)
    } catch (error) {
      console.error('Error searching employees:', error)
    } finally {
      loading(false)
    }
  }

  getSelectedAccounts(): Account[] {
    const workersComponent = this.$refs.workersComponent as InstanceType<typeof AccountWorkers>
    if (workersComponent) {
      return workersComponent.getSelectedAccounts()
    }
    return []
  }

  async addWorkers(payload: { selectedManagerIds: string[]; setAdding: (value: boolean) => void; clearSelection: () => void }) {
    const { selectedManagerIds, setAdding, clearSelection } = payload

    if (selectedManagerIds.length === 0) {
      this.$bvToast.toast(this.$t('account_detail.workers.messages.select_manager').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'warning',
        solid: true
      })
      return
    }

    setAdding(true)

    try {
      let successCount = 0
      let failCount = 0
      const errors: string[] = []

      for (const employeeId of selectedManagerIds) {
        try {
          await ApiClient.createAccountManager(employeeId, this.account.accountId)
          successCount++
        } catch (error: any) {
          failCount++
          errors.push(employeeId)
          console.error(`Error adding employee ${employeeId}:`, error)
        }
      }

      if (successCount > 0) {
        const message = successCount === 1
          ? this.$t('account_detail.workers.messages.worker_added').toString()
          : this.$t('account_detail.workers.messages.workers_added', { count: successCount }).toString()

        this.$bvToast.toast(message, {
          title: this.$t('feedback.feedback_message_success_title').toString(),
          variant: 'success',
          solid: true
        })
      }

      if (failCount > 0) {
        const message = failCount === 1
          ? this.$t('account_detail.workers.messages.error_adding_worker', { worker: errors[0] }).toString()
          : this.$t('account_detail.workers.messages.error_adding_workers', { count: failCount, workers: errors.join(', ') }).toString()

        this.$bvToast.toast(message, {
          title: this.$t('account_detail.error_title').toString(),
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      }

      clearSelection()
      this.lastManagerSearch = ''
      await this.loadAccount()
    } catch (error: any) {
      console.error('Error adding workers:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.workers.messages.error_adding').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      setAdding(false)
    }
  }

  async deleteWorker(accountManager: AccountManager) {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_detail.workers.messages.confirm_delete', { manager: accountManager.accountId }).toString(),
      {
        title: this.$t('account_detail.workers.messages.confirm_delete_title').toString(),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      await ApiClient.deleteAccountManager(accountManager.accountManagerId)

      this.$bvToast.toast(this.$t('account_detail.workers.messages.worker_deleted').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error deleting worker:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.workers.messages.error_deleting').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  async deleteAllWorkers() {
    if (!this.workers || this.workers.length === 0) return

    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_detail.workers.messages.confirm_delete_all', { count: this.workers.length }).toString(),
      {
        title: this.$t('account_detail.workers.messages.confirm_delete_all_title').toString(),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      for (const worker of this.workers) {
        await ApiClient.deleteAccountManager(worker.accountManagerId)
      }

      this.$bvToast.toast(this.$t('account_detail.workers.messages.all_workers_deleted').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error deleting all workers:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.workers.messages.error_deleting_all').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })

      await this.loadAccount()
    }
  }

  // Teams Methods
  async addTeams(payload: { selectedTeamIds: number[]; setAdding: (value: boolean) => void; clearSelection: () => void }) {
    const { selectedTeamIds, setAdding, clearSelection } = payload

    if (selectedTeamIds.length === 0) {
      this.$bvToast.toast(this.$t('account_detail.teams.messages.select_team').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'warning',
        solid: true
      })
      return
    }

    setAdding(true)

    try {
      let successCount = 0
      let failCount = 0
      const errors: number[] = []

      for (const teamId of selectedTeamIds) {
        try {
          await ApiClient.createAccountTeam(this.account.accountId, teamId)
          successCount++
        } catch (error: any) {
          failCount++
          errors.push(teamId)
          console.error(`Error adding team ${teamId}:`, error)
        }
      }

      if (successCount > 0) {
        const message = successCount === 1
          ? this.$t('account_detail.teams.messages.team_added').toString()
          : this.$t('account_detail.teams.messages.teams_added', { count: successCount }).toString()

        this.$bvToast.toast(message, {
          title: this.$t('feedback.feedback_message_success_title').toString(),
          variant: 'success',
          solid: true
        })
      }

      if (failCount > 0) {
        this.$bvToast.toast(
          this.$t('account_detail.teams.messages.error_adding_teams', { count: failCount, ids: errors.join(', ') }).toString(),
          {
            title: this.$t('account_detail.error_title').toString(),
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        )
      }

      clearSelection()
      await this.loadAccount()
    } catch (error: any) {
      console.error('Error adding teams:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.teams.messages.error_adding').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      setAdding(false)
    }
  }

  async deleteTeam(accountTeam: AccountTeam) {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_detail.teams.messages.confirm_delete', { team: accountTeam.teamId }).toString(),
      {
        title: this.$t('account_detail.teams.messages.confirm_delete_title').toString(),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      await ApiClient.deleteAccountTeam(accountTeam.id)

      this.$bvToast.toast(this.$t('account_detail.teams.messages.team_deleted').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error deleting team:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.teams.messages.error_deleting').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  async deleteAllTeams() {
    if (!this.teams || this.teams.length === 0) return

    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_detail.teams.messages.confirm_delete_all', { count: this.teams.length }).toString(),
      {
        title: this.$t('account_detail.teams.messages.confirm_delete_all_title').toString(),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )

    if (!confirmed) return

    try {
      for (const t of this.teams) {
        await ApiClient.deleteAccountTeam(t.id)
      }

      this.$bvToast.toast(this.$t('account_detail.teams.messages.all_teams_deleted').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })

      await this.loadAccount()
    } catch (error: any) {
      console.error('Error deleting all teams:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.teams.messages.error_deleting_all').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })

      await this.loadAccount()
    }
  }
}
